import type { IDIALOGVERIFYTRANSACTIONPROPS } from 'BreetComponents';
import { FIXEDCACHEKEYS, showToast, type TwoFaType } from 'BreetHelpers';
import { useModalApi } from 'BreetHooks';
import {
	useConvertFiatToUSDMutation,
	useDisableAutoSettlementMutation,
	useRequestPaymentMutation,
	useSaveWalletAddressMutation,
	useSellUSDMutation,
	useSwapCurrencyMutation,
	useUpdateAutoSettlementMutation,
	useWithdrawUSDMutation,
} from 'BreetRedux';
import { useCallback } from 'react';

export const useMfaTransactionsApi = () => {
	const { getModalProps, closeModalDirect, handleModalCloseAll } = useModalApi();

	const [requestPayment, { isLoading: requestPaymentLoading }] = useRequestPaymentMutation();
	const [withdrawUSD, { isLoading: withdrawUSDLoading }] = useWithdrawUSDMutation();
	const [convertFiatToUSD, { isLoading: convertFiatToUSDLoading }] = useConvertFiatToUSDMutation();
	const [swapCurrency, { isLoading: swapCurrencyLoading }] = useSwapCurrencyMutation();
	const [saveWalletAddress, { isLoading: saveWalletAddressLoading, reset }] = useSaveWalletAddressMutation({
		fixedCacheKey: FIXEDCACHEKEYS.SAVE_WALLET_ADDRESS,
	});
	const [updateAutoSettlement, { isLoading: updateAutoSettlementLoading }] = useUpdateAutoSettlementMutation();
	const [disableAutoSettlement, { isLoading: disableAutoSettlementLoading }] = useDisableAutoSettlementMutation();
	const [sellUSD, { isLoading: sellUSDLoading }] = useSellUSDMutation();

	const modalProps = getModalProps('verifyTransaction');

	const {
		transactionType,
		skipPin,
		withdrawFiatReqData,
		withdrawUSDReqData,
		convertFiatToUSDReqData,
		swapCurrencyReqData,
		saveWalletAddressReqData,
		updateAutoSettlementReqData,
		disableAutoSettlementReqData,
		sellUsdReqData,
	} = (modalProps?.metadata ?? {}) as IDIALOGVERIFYTRANSACTIONPROPS;

	const fetchOTP = useCallback(
		(pinArg: string, otpArg?: TwoFaType) => {
			const withOtp = <T extends object>(body: T): T & { otp?: TwoFaType } => (otpArg ? { ...body, otp: otpArg } : body);

			const requestBuilders: Partial<Record<typeof transactionType, () => Promise<unknown>>> = {
				'withdraw-fiat': withdrawFiatReqData && (() => requestPayment(withOtp({ ...withdrawFiatReqData, pin: pinArg })).unwrap()),
				'withdrawUSD': withdrawUSDReqData && (() => withdrawUSD(withOtp({ ...withdrawUSDReqData, pin: pinArg })).unwrap()),
				'convertFiatToUSD': convertFiatToUSDReqData && (() => convertFiatToUSD(withOtp({ ...convertFiatToUSDReqData })).unwrap()),
				'swapCurrency': swapCurrencyReqData && (() => swapCurrency(withOtp({ ...swapCurrencyReqData, pin: pinArg })).unwrap()),
				'saveWalletAddress': saveWalletAddressReqData && (() => saveWalletAddress(withOtp(saveWalletAddressReqData)).unwrap()),
				'updateAutoSettlement':
					updateAutoSettlementReqData && (() => updateAutoSettlement(withOtp(updateAutoSettlementReqData)).unwrap()),
				'disableAutoSettlement':
					disableAutoSettlementReqData && (() => disableAutoSettlement(withOtp(disableAutoSettlementReqData)).unwrap()),
				'sell-usd': sellUsdReqData && (() => sellUSD(withOtp(sellUsdReqData)).unwrap()),
			};

			const apiRequest = requestBuilders[transactionType]?.();

			if (!apiRequest) {
				showToast('error', 'Something went wrong');
				return;
			}

			return apiRequest;
		},
		[
			withdrawFiatReqData,
			withdrawUSDReqData,
			convertFiatToUSDReqData,
			swapCurrencyReqData,
			saveWalletAddressReqData,
			updateAutoSettlementReqData,
			disableAutoSettlementReqData,
			sellUsdReqData,
			transactionType,
			requestPayment,
			withdrawUSD,
			convertFiatToUSD,
			swapCurrency,
			saveWalletAddress,
			updateAutoSettlement,
			disableAutoSettlement,
			sellUSD,
		]
	);

	const closeModal = useCallback(() => {
		closeModalDirect('loadingDialog');

		if (transactionType === 'saveWalletAddress') {
			reset();
			closeModalDirect('verifyTransaction');
			return;
		}

		handleModalCloseAll();
	}, [closeModalDirect, handleModalCloseAll, reset, transactionType]);

	return {
		fetchOTP,
		transactionType,
		skipPin,
		closeModal,
		isApiloading:
			requestPaymentLoading ||
			withdrawUSDLoading ||
			convertFiatToUSDLoading ||
			swapCurrencyLoading ||
			saveWalletAddressLoading ||
			updateAutoSettlementLoading ||
			disableAutoSettlementLoading ||
			sellUSDLoading,
	};
};
